import { useEffect, useMemo, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { apiJson } from '../lib/multiplayer';
import { useAuthStore } from '../lib/auth';

interface DocResponse {
  slug: string;
  content: string;
  updatedAt: number;
  updatedBy: string | null;
}

export function DocPageRoute() {
  const params = useParams({ from: '/docs/$slug' });
  const slug = params.slug.replace(/\.md$/i, '');
  const user = useAuthStore((s) => s.user);
  const isOwner = Boolean(user?.canAdmin && user?.username === 'FM');
  const [doc, setDoc] = useState<DocResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    apiJson<DocResponse>(`/api/docs/${encodeURIComponent(slug)}`)
      .then((d) => {
        if (alive) {
          setDoc(d);
          setDraft(d.content);
          setLoaded(true);
        }
      })
      .catch((err: Error) => {
        if (alive) {
          setError(err.message);
          setLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  async function save() {
    if (!isOwner) return;
    setSaving(true);
    try {
      const updated = await apiJson<DocResponse>(`/api/docs/${encodeURIComponent(slug)}`, {
        method: 'PUT',
        body: JSON.stringify({ content: draft }),
      });
      setDoc(updated);
      setEditing(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (!loaded) {
    return (
      <div className="min-h-full w-full max-w-[900px] mx-auto px-5 md:px-10 py-6 pb-12 grid place-items-center">
        <p className="text-sm text-ink-muted">Loading…</p>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-full w-full max-w-[900px] mx-auto px-5 md:px-10 py-6 pb-12 grid place-items-center">
        <Panel className="p-6 text-center">
          <div className="font-display text-2xl font-bold">Page not found</div>
          <p className="mt-2 text-sm text-ink-muted">{error ?? 'No doc with that name.'}</p>
        </Panel>
      </div>
    );
  }

  const updated = new Date(doc.updatedAt);
  const updatedLabel = updated.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  return (
    <div className="min-h-full w-full max-w-[900px] mx-auto px-5 md:px-10 py-6 pb-12 flex flex-col gap-4">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <p className="text-xs text-ink-muted">Last updated {updatedLabel}</p>
        {isOwner && !editing && (
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
        {isOwner && editing && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setDraft(doc.content);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>
      {error && (
        <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {editing ? (
        <Panel className="p-4">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full min-h-[60vh] font-mono text-sm bg-surface-2 border border-ink/10 rounded-md p-3 focus-ring resize-y"
            spellCheck={false}
          />
          <p className="mt-2 text-xs text-ink-muted">
            Markdown supported: <code># headings</code>, <code>**bold**</code>, <code>*italic*</code>,
            lists, <code>&gt; blockquote</code>, and <code>`code`</code>.
          </p>
        </Panel>
      ) : (
        <Panel className="p-5 md:p-7">
          <MarkdownContent source={doc.content} />
        </Panel>
      )}
    </div>
  );
}

function MarkdownContent({ source }: { source: string }) {
  const nodes = useMemo(() => renderMarkdown(source), [source]);
  return <div className="prose-doc">{nodes}</div>;
}

// Minimal, dependency-free markdown renderer covering: # h1..h3, p, ul/ol,
// blockquote, **bold**, *italic*, `code`. Sanitizes by rendering only
// known tags; no raw HTML pass-through.
function renderMarkdown(src: string): JSX.Element[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: JSX.Element[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';
    if (line.trim() === '') {
      i++;
      continue;
    }
    if (line.startsWith('# ')) {
      out.push(
        <h1 key={key++} className="font-display text-3xl font-bold mt-6 first:mt-0">
          {renderInline(line.slice(2), 0)}
        </h1>,
      );
      i++;
      continue;
    }
    if (line.startsWith('## ')) {
      out.push(
        <h2 key={key++} className="font-display text-2xl font-bold mt-6">
          {renderInline(line.slice(3), 0)}
        </h2>,
      );
      i++;
      continue;
    }
    if (line.startsWith('### ')) {
      out.push(
        <h3 key={key++} className="font-display text-xl font-bold mt-5">
          {renderInline(line.slice(4), 0)}
        </h3>,
      );
      i++;
      continue;
    }
    if (line.startsWith('> ')) {
      const buf: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('> ')) {
        buf.push((lines[i] ?? '').slice(2));
        i++;
      }
      out.push(
        <blockquote
          key={key++}
          className="mt-4 border-l-4 border-accent/40 bg-accent/[0.04] pl-4 py-2 text-ink-muted italic"
        >
          {renderInline(buf.join(' '), 0)}
        </blockquote>,
      );
      continue;
    }
    if (/^[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^[-*]\s/, ''));
        i++;
      }
      out.push(
        <ul key={key++} className="mt-3 list-disc pl-6 space-y-1.5 text-ink-muted">
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item, 0)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i] ?? '')) {
        items.push((lines[i] ?? '').replace(/^\d+\.\s/, ''));
        i++;
      }
      out.push(
        <ol key={key++} className="mt-3 list-decimal pl-6 space-y-1.5 text-ink-muted">
          {items.map((item, idx) => (
            <li key={idx}>{renderInline(item, 0)}</li>
          ))}
        </ol>,
      );
      continue;
    }
    // paragraph: gather lines until blank
    const buf: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !/^(#{1,3}\s|>\s|[-*]\s|\d+\.\s)/.test(lines[i] ?? '')
    ) {
      buf.push(lines[i] ?? '');
      i++;
    }
    out.push(
      <p key={key++} className="mt-3 text-ink-muted leading-relaxed">
        {renderInline(buf.join(' '), 0)}
      </p>,
    );
  }
  return out;
}

function renderInline(src: string, baseKey: number): React.ReactNode[] {
  // Tokenize: bold (**x**), italic (*x*), code (`x`), and plain text.
  const out: React.ReactNode[] = [];
  let i = 0;
  let key = baseKey;
  let buf = '';
  const flushText = () => {
    if (buf) {
      out.push(buf);
      buf = '';
    }
  };
  while (i < src.length) {
    if (src.startsWith('**', i)) {
      const end = src.indexOf('**', i + 2);
      if (end > i + 2) {
        flushText();
        out.push(
          <strong key={`b-${key++}`} className="text-ink font-semibold">
            {src.slice(i + 2, end)}
          </strong>,
        );
        i = end + 2;
        continue;
      }
    }
    if (src[i] === '*' && src[i + 1] !== '*') {
      const end = src.indexOf('*', i + 1);
      if (end > i + 1) {
        flushText();
        out.push(
          <em key={`i-${key++}`} className="italic">
            {src.slice(i + 1, end)}
          </em>,
        );
        i = end + 1;
        continue;
      }
    }
    if (src[i] === '`') {
      const end = src.indexOf('`', i + 1);
      if (end > i + 1) {
        flushText();
        out.push(
          <code
            key={`c-${key++}`}
            className="rounded bg-ink/[0.06] px-1 py-0.5 font-mono text-[0.9em] text-ink"
          >
            {src.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    buf += src[i];
    i++;
  }
  flushText();
  return out;
}
